import { useState, useRef, useEffect } from "react";
import { Bell, Mic, Sparkles, ChevronLeft, ChevronRight, ExternalLink } from "lucide-react";

type TrainingType = "observation" | "conversation" | "feeling" | "echo";

interface TrainingScenario {
  type: TrainingType;
  badge: string;
  badgeEmoji: string;
  title: string;
  subtitle?: string;
  prompt: string;
  promptLines: string[];
  demoInput: string;
  echoResponse: string;
  echoHighlight: string;
  explanation: string[];
  inspirations: { content: string; likes: number; emoji: string }[];
  ammoQuote?: { content: string; source: string };
  abilitySummary: string;
}

const scenarios: TrainingScenario[] = [
  {
    type: "observation",
    badge: "观察",
    badgeEmoji: "🌸",
    title: "傍晚的海边",
    prompt: "请不要说「好美」。试着用一句真正属于你的话描述这一刻。",
    promptLines: ["请不要说「好美」。", "试着用一句真正属于你的话描述这一刻。"],
    demoInput: "海边真的好美，让人一下子安静了。",
    echoResponse: "海边真的好美。夕阳把海浪边缘照得像一层慢慢融化的金箔，连风都像放轻了声音。",
    echoHighlight: "夕阳把海浪边缘照得像一层慢慢融化的金箔，连风都像放轻了声音。",
    explanation: [
      "你已经表达了自己的感受。",
      "如果再加入一个能够被看见的细节，",
      "别人会更容易进入你眼里的那个画面。",
    ],
    inspirations: [
      { content: "海风没有说话，却把白天吹得越来越轻。", likes: 126, emoji: "🌊" },
      { content: "原来夕阳不是落下去了，而是慢慢沉进了海里。", likes: 89, emoji: "🍃" },
      { content: "那一刻我突然觉得，世界终于比手机屏幕大了一点。", likes: 214, emoji: "🌙" },
    ],
    abilitySummary: "先表达感受，再表达细节。",
  },
  {
    type: "conversation",
    badge: "回应",
    badgeEmoji: "💬",
    title: "朋友说",
    subtitle: "最近什么都不想干。",
    prompt: "如果这是你最好的朋友，你的第一反应会是什么？",
    promptLines: ["朋友说：", "最近什么都不想干。", "如果这是你最好的朋友，", "你的第一反应会是什么？"],
    demoInput: "没事啦，都会过去的。",
    echoResponse: "听起来，你最近真的有点累。不着急好起来，我陪你慢慢来。",
    echoHighlight: "听起来，你最近真的有点累。",
    explanation: [
      "很多时候，",
      "别人需要的不是解决办法，",
      "而是先有人理解他的感受。",
      "先回应情绪，再给予陪伴，",
      "会比直接安慰更容易让人安心。",
    ],
    inspirations: [
      { content: "如果今天什么都不想做，那我们今天就什么都不做。", likes: 301, emoji: "🌿" },
      { content: "我不知道怎么让你好起来，但我可以陪你坐一会儿。", likes: 247, emoji: "☁️" },
      { content: "辛苦了，你已经撑了很久吧。", likes: 198, emoji: "🍂" },
    ],
    abilitySummary: "先回应情绪，再表达观点。",
  },
  {
    type: "feeling",
    badge: "感受",
    badgeEmoji: "🌱",
    title: "收到夸奖",
    prompt: "请不要回答「谢谢。」试着说说，你真正的感受。",
    promptLines: ["今天收到一句夸奖。", "请不要回答：", "\"谢谢。\"", "试着说说，你真正的感受。"],
    demoInput: "谢谢，其实我也没有那么好。",
    echoResponse: "谢谢你这样说。听到这句话的时候，我其实挺开心的，也让我更有继续努力的动力。",
    echoHighlight: "听到这句话的时候，我其实挺开心的，也让我更有继续努力的动力。",
    explanation: [
      "接受一份善意，并不是骄傲。",
      "表达自己的真实感受，",
      "也能让对方知道，",
      "他的夸奖真的被你接住了。",
    ],
    inspirations: [
      { content: "我会因为这句话开心一整天。", likes: 172, emoji: "🌸" },
      { content: "谢谢你注意到了我的努力。", likes: 206, emoji: "🌼" },
      { content: "原来我也可以成为别人眼里的光。", likes: 318, emoji: "✨" },
    ],
    abilitySummary: "接受夸奖时，说出你的真实感受。",
  },
  {
    type: "echo",
    badge: "化用",
    badgeEmoji: "✨",
    title: "来自你的弹药库",
    subtitle: "人和人的相遇，本来就是一场彼此照亮。——《云边有个小卖部》",
    prompt: "今天，一位很久没联系的朋友突然给你发来消息。试着自然地把这句话，变成一句属于你自己的表达。",
    promptLines: ["今天，", "一位很久没联系的朋友突然给你发来消息。", "试着自然地把这句话，", "变成一句属于你自己的表达。"],
    demoInput: "好久不见，突然看到你的消息真的很开心。",
    echoResponse: "好久不见。看到你的消息真的很开心。你突然出现，好像又把那段很珍贵的时光轻轻照亮了一次。",
    echoHighlight: "你突然出现，好像又把那段很珍贵的时光轻轻照亮了一次。",
    explanation: [
      "你没有直接引用收藏的话。",
      "而是把它变成了自己的表达。",
      "这就是 Echo 希望帮助你做到的：",
      "不是记住一句话，而是让它慢慢长成你的语言。",
    ],
    inspirations: [
      { content: "原来有些人，就算很久没见，再出现的时候还是会发光。", likes: 186, emoji: "🌱" },
      { content: "谢谢你又出现在我的生活里。", likes: 143, emoji: "🌤" },
      { content: "原来真正的重逢，不需要太多寒暄。", likes: 264, emoji: "🌸" },
    ],
    ammoQuote: { content: "人和人的相遇，本来就是一场彼此照亮。", source: "——《云边有个小卖部》" },
    abilitySummary: "把别人的智慧，变成自己的语言。",
  },
];

const dailyQuotes = [
  "今天，不急着回答世界。",
  "先练习，把心里的话说清楚。",
];

export default function TargetPage() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [firstInput, setFirstInput] = useState("");
  const [submittedFirst, setSubmittedFirst] = useState(false);
  const feedbackRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const currentScenario = scenarios[currentIndex];

  useEffect(() => {
    if (submittedFirst && feedbackRef.current) {
      feedbackRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [submittedFirst]);

  useEffect(() => {
    setFirstInput("");
    setSubmittedFirst(false);
  }, [currentIndex]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? scenarios.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === scenarios.length - 1 ? 0 : prev + 1));
  };

  const handleRetry = () => {
    setCurrentIndex((prev) => (prev === scenarios.length - 1 ? 0 : prev + 1));
  };

  const scrollInspirations = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="relative min-h-full">
      {/* Floating glow orbs */}
      <div className="glow-orb" style={{ width: 300, height: 300, background: "#A8E6CF", top: -100, right: -120, opacity: 0.65 }} />
      <div className="glow-orb" style={{ width: 260, height: 260, background: "#FFD0DD", top: 240, left: -100, opacity: 0.55 }} />
      <div className="glow-orb" style={{ width: 220, height: 220, background: "#DDF6E8", top: 760, right: -80, opacity: 0.45 }} />
      <div className="glow-orb" style={{ width: 200, height: 200, background: "#FFE4EC", top: 460, left: 10, opacity: 0.4 }} />

      {/* Header */}
      <header className="relative px-5 pt-5 pb-3" style={{ minHeight: 80 }}>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-[24px] font-bold text-echo-text leading-tight">🌸 今日表达</h1>
            <p className="text-echo-text/45 text-[13px] mt-1.5">{dailyQuotes[0]}</p>
            <p className="text-echo-text/35 text-[12px]">{dailyQuotes[1]}</p>
          </div>
          <button className="icon-btn">
            <Bell size={17} strokeWidth={2} className="text-echo-text/70" />
          </button>
        </div>
      </header>

      {/* Navigation arrows */}
      <div className="flex items-center justify-between px-5 mb-3">
        <button onClick={handlePrev} className="icon-btn">
          <ChevronLeft size={18} strokeWidth={2} className="text-echo-text/50" />
        </button>
        <div className="flex gap-1.5">
          {scenarios.map((_, i) => (
            <div
              key={i}
              className="w-1.5 h-1.5 rounded-full transition-all"
              style={{
                background: i === currentIndex ? "#A8E6CF" : "rgba(46, 42, 57, 0.08)",
                opacity: i === currentIndex ? 1 : 0.4,
              }}
            />
          ))}
        </div>
        <button onClick={handleNext} className="icon-btn">
          <ChevronRight size={18} strokeWidth={2} className="text-echo-text/50" />
        </button>
      </div>

      {/* Hero Card */}
      <section className="px-5 pb-4">
        <div className="glass-card rounded-card overflow-hidden relative animate-scale-in">
          {/* Badge */}
          <div className="absolute top-4 left-4 z-10">
            <span className="px-3 py-1.5 rounded-full text-[12px] font-medium bg-white/50 text-echo-text/70 flex items-center gap-1">
              <span>{currentScenario.badgeEmoji}</span>
              {currentScenario.badge}
            </span>
          </div>

          {/* Type indicator */}
          {currentScenario.type === "echo" && (
            <div className="absolute top-4 right-4 z-10">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-[#F2EAFF]/40 text-[#8B5CF6]">
                ✨ 已优先调用你的弹药库
              </span>
            </div>
          )}

          {/* Content */}
          <div className="p-6 pt-16">
            <h2 className="text-[18px] font-bold text-echo-text mb-2">{currentScenario.title}</h2>
            {currentScenario.subtitle && (
              <p className="text-echo-text/50 text-[13px] mb-6 italic">{currentScenario.subtitle}</p>
            )}
            
            <div className="mb-6">
              {currentScenario.promptLines.map((line, i) => (
                <p key={i} className="text-echo-text text-[15px] leading-relaxed mb-1">
                  {line}
                </p>
              ))}
            </div>

            {/* Input area */}
            {!submittedFirst ? (
              <>
                <div className="mb-3">
                  <p className="text-[12px] text-echo-text/40 flex items-center gap-1">
                    <span>💡</span>
                    DEMO演示阶段:待会会这样输入然后 Echo 给建议
                  </p>
                </div>
                <div className="relative mb-4">
                  <textarea
                    value={firstInput || currentScenario.demoInput}
                    onChange={(e) => setFirstInput(e.target.value)}
                    className="w-full px-4 py-3.5 rounded-3xl bg-white/35 backdrop-blur-xl text-[15px] resize-none focus:outline-none transition-all border border-white/40"
                    style={{ 
                      height: 110,
                      color: firstInput ? "#2E2A39" : "rgba(107, 102, 128, 0.45)",
                      fontStyle: firstInput ? "normal" : "italic",
                    }}
                  />
                  <button className="absolute right-3 bottom-3 w-9 h-9 rounded-full bg-white/40 flex items-center justify-center animate-spring">
                    <Mic size={16} strokeWidth={2} className="text-echo-text/50" />
                  </button>
                </div>
                <button
                  onClick={() => setSubmittedFirst(true)}
                  className="w-full h-44 rounded-3xl bg-gradient-to-r from-[#DDF6E8] to-[#FFD0DD] text-echo-text font-semibold text-[15px] transition-all animate-spring flex items-center justify-center gap-2"
                  style={{ boxShadow: "0 8px 24px rgba(168, 230, 207, 0.25)" }}
                >
                  <Sparkles size={16} strokeWidth={2} />
                  确认这个表达
                </button>
              </>
            ) : null}
          </div>
        </div>
      </section>

      {/* AI Feedback */}
      {submittedFirst && (
        <section ref={feedbackRef} className="px-5 pb-4 animate-fade-slide">
          <h2 className="text-[17px] font-bold text-echo-text mb-3 flex items-center gap-1.5">
            <Sparkles size={17} strokeWidth={2} className="text-[#A8E6CF]" />
            一起看看还能怎么表达
          </h2>

          {/* Your expression */}
          <div className="glass-card rounded-card p-5 mb-4">
            <p className="text-[12px] font-medium text-echo-text/40 mb-2">你的表达</p>
            <p className="text-echo-text text-[15px] leading-relaxed italic">
              "{firstInput}"
            </p>
          </div>

          {/* Echo suggestion */}
          <div className="glass-card rounded-card p-5 mb-4">
            <p className="text-[12px] font-medium text-echo-text/40 mb-2">Echo 建议</p>
            <p className="text-echo-text text-[15px] leading-relaxed">
              {currentScenario.echoResponse.split(currentScenario.echoHighlight).map((part, i) => (
                <span key={i}>
                  {part}
                  {i < 1 && (
                    <span className="bg-[#DDF6E8]/30 px-0.5 rounded">{currentScenario.echoHighlight}</span>
                  )}
                </span>
              ))}
            </p>
          </div>

          {/* Why */}
          <div className="glass-card rounded-card p-5 mb-4">
            <p className="text-[12px] font-medium text-echo-text/40 mb-3 flex items-center gap-1">
              <span>🌱</span> 为什么这样表达
            </p>
            {currentScenario.explanation.map((line, i) => (
              <p key={i} className="text-echo-text/65 text-[13px] leading-relaxed mb-1">
                {line}
              </p>
            ))}
          </div>

          {/* Inspiration cards */}
          <div className="relative">
            <h3 className="text-[15px] font-medium text-echo-text/70 mb-3">如果想看看别人怎么表达</h3>
            <p className="text-[12px] text-echo-text/40 mb-3">今天还有这些表达</p>
            
            <div className="flex items-center gap-2">
              <button onClick={() => scrollInspirations("left")} className="icon-btn">
                <ChevronLeft size={16} strokeWidth={2} className="text-echo-text/50" />
              </button>
              <div
                ref={scrollContainerRef}
                className="flex-1 flex gap-3 overflow-x-auto scrollbar-hide pb-2"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {currentScenario.inspirations.map((item, i) => (
                  <div
                    key={i}
                    className="glass-card rounded-card p-4 flex-shrink-0"
                    style={{ width: 200 }}
                  >
                    <span className="text-[16px] mb-2 block">{item.emoji}</span>
                    <p className="text-echo-text/70 text-[13px] leading-relaxed mb-3">
                      {item.content}
                    </p>
                    <div className="flex items-center gap-1 text-echo-text/40 text-[11px]">
                      <span>❤️</span>
                      {item.likes}
                    </div>
                  </div>
                ))}
              </div>
              <button onClick={() => scrollInspirations("right")} className="icon-btn">
                <ChevronRight size={16} strokeWidth={2} className="text-echo-text/50" />
              </button>
            </div>

            {/* Ammo quote indicator for echo type */}
            {currentScenario.type === "echo" && (
              <div className="mt-4 px-4 py-2.5 rounded-full bg-[#F2EAFF]/20 flex items-center justify-between">
                <span className="text-[12px] text-echo-text/60">
                  🌿 今天你自然化用了 1 条自己的收藏表达
                </span>
                <button className="flex items-center gap-1 text-[11px] text-[#8B5CF6]">
                  <ExternalLink size={10} strokeWidth={2} />
                  <span>查看来源</span>
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Ability summary card */}
      {submittedFirst && (
        <section className="px-5 pb-4 animate-fade-slide">
          <div className="glass-card rounded-card p-4">
            <div className="flex items-center gap-2">
              <span className="text-[16px]">🌿</span>
              <span className="text-[13px] font-medium text-echo-text/60">今天你练到的是：</span>
            </div>
            <p className="text-echo-text font-semibold text-[15px] mt-2 pl-6">
              「{currentScenario.abilitySummary}」
            </p>
            <p className="text-echo-text/35 text-[12px] mt-2 pl-6">
              这会成为你下一次表达时的本能。
            </p>
          </div>
        </section>
      )}

      {/* Product philosophy */}
      <section className="px-5 pb-6">
        <p className="text-center text-echo-text/30 text-[12px]">
          每一句认真说出口的话，都会成为未来脱口而出的本能。
        </p>
      </section>

      {/* Retry button */}
      {submittedFirst && (
        <section className="px-5 pb-20">
          <button
            onClick={handleRetry}
            className="w-full h-12 rounded-full bg-white/35 backdrop-blur-xl text-echo-text/60 font-medium text-[14px] transition-all hover:bg-white/45 animate-spring flex items-center justify-center gap-2"
          >
            <span>🌿</span>
            再练一句
          </button>
        </section>
      )}
    </div>
  );
}